import {
  Directive,
  ElementRef,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  Renderer2,
  NgZone,
  inject,
  ViewContainerRef
} from '@angular/core';
import {Overlay, OverlayRef, ConnectedPosition} from '@angular/cdk/overlay';
import {ComponentPortal} from '@angular/cdk/portal';
import {Component} from '@angular/core';

@Component({
  selector: 'ui-tooltip-overlay',
  standalone: true,
  template: `
    <div class="ui-tooltip-overlay" role="tooltip">{{ text }}</div>
  `,
  styles: [
    `
      .ui-tooltip-overlay {
        pointer-events: none;
        padding: 0.35rem 0.5rem;
        border-radius: 0.375rem;
        font-size: 0.75rem;
        line-height: 1.1;
        max-width: 240px;
        white-space: pre-wrap;
        /* DaisyUI tokens, but with fallbacks to avoid IDE warnings */
        background: hsl(var(--n, 0 0% 15%));
        color: hsl(var(--nc, 0 0% 98%));
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
        z-index: 10000;
      }
    `
  ]
})
class UiTooltipOverlayComponent {
  @Input() text = '';
}

@Directive({
  selector: '[uiTooltip]',
  standalone: true
})
export class UiTooltipDirective implements OnInit, OnChanges, OnDestroy {
  @Input() uiTooltip: string = '';
  @Input() tooltipPosition: 'top' | 'bottom' | 'left' | 'right' = 'top';

  private overlayRef: OverlayRef | null = null;
  private portalRef: any | null = null;

  private removeEnter?: () => void;
  private removeLeave?: () => void;
  private removeFocus?: () => void;
  private removeBlur?: () => void;

  private readonly overlay = inject(Overlay);
  private readonly vcr = inject(ViewContainerRef);
  private readonly zone = inject(NgZone);

  constructor(
    private el: ElementRef,
    private renderer: Renderer2
  ) {
  }

  ngOnInit() {
    // Keep a11y hint
    this.renderer.setAttribute(this.el.nativeElement, 'aria-label', this.uiTooltip || '');

    // Mouse + keyboard
    this.zone.runOutsideAngular(() => {
      this.removeEnter = this.renderer.listen(this.el.nativeElement, 'mouseenter', () => this.show());
      this.removeLeave = this.renderer.listen(this.el.nativeElement, 'mouseleave', () => this.hide());
      this.removeFocus = this.renderer.listen(this.el.nativeElement, 'focusin', () => this.show());
      this.removeBlur = this.renderer.listen(this.el.nativeElement, 'focusout', () => this.hide());
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['uiTooltip']) {
      this.renderer.setAttribute(this.el.nativeElement, 'aria-label', this.uiTooltip || '');
      // Keep live tooltip text updated
      const inst: UiTooltipOverlayComponent | undefined = this.portalRef?.instance;
      if (inst) inst.text = this.uiTooltip || '';

      if (!this.uiTooltip) {
        this.hide();
      }
    }

    if (changes['tooltipPosition'] && this.overlayRef) {
      // Recreate overlay with new position mapping.
      const wasAttached = this.overlayRef.hasAttached();
      this.destroyOverlay();
      if (wasAttached) {
        this.show();
      }
    }
  }

  private show() {
    if (!this.uiTooltip || !this.uiTooltip.trim()) return;

    if (!this.overlayRef) {
      this.overlayRef = this.overlay.create({
        positionStrategy: this.overlay
          .position()
          .flexibleConnectedTo(this.el)
          .withPositions(this.connectedPositions())
          .withFlexibleDimensions(false)
          .withPush(true),
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        hasBackdrop: false
      });
    }

    if (!this.overlayRef.hasAttached()) {
      const portal = new ComponentPortal(UiTooltipOverlayComponent, this.vcr);
      this.portalRef = this.overlayRef.attach(portal);
      this.portalRef.instance.text = this.uiTooltip;
    } else {
      const inst: UiTooltipOverlayComponent | undefined = this.portalRef?.instance;
      if (inst) inst.text = this.uiTooltip;
    }
  }

  private hide() {
    if (this.overlayRef?.hasAttached()) {
      this.overlayRef.detach();
    }
  }

  private connectedPositions(): ConnectedPosition[] {
    // Small gap between origin and tooltip
    const offset = 8;

    const top: ConnectedPosition = {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      offsetY: -offset
    };

    const bottom: ConnectedPosition = {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      offsetY: offset
    };

    const left: ConnectedPosition = {
      originX: 'start',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'center',
      offsetX: -offset
    };

    const right: ConnectedPosition = {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
      offsetX: offset
    };

    // Provide fallbacks so we still show if near viewport edge
    switch (this.tooltipPosition) {
      case 'bottom':
        return [bottom, top, right, left];
      case 'left':
        return [left, right, top, bottom];
      case 'right':
        return [right, left, top, bottom];
      case 'top':
      default:
        return [top, bottom, right, left];
    }
  }

  private destroyOverlay() {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
      this.portalRef = null;
    }
  }

  ngOnDestroy() {
    this.hide();
    this.destroyOverlay();

    this.removeEnter?.();
    this.removeLeave?.();
    this.removeFocus?.();
    this.removeBlur?.();

    this.renderer.removeAttribute(this.el.nativeElement, 'aria-label');
  }
}
